<template>
  <DataTable
    ref="dataTable"
    :columns="columnList"
    defaultColumnOrderDir="DESC"
    :defaultColumnOrderIndex="12"
    :url="dataURL"
    :filterOptions="filterOptions"
    :locationFilterOptions="locationFilterOptions"
    :displayLocationFilter="true"
    :displayTools="true"
    :hasDateRangeFilter="true"
    :displayExportTools="true"
    exportTitle="Stock Transactions">

    <template v-slot:title>
      <slot name="title" />
    </template>
    <template v-slot:tools>
      <slot name="tools" />
    </template>
    <template v-slot:row="row">
      <tr :key="row.data.id">
        <td class="table-data-nav-link">
          <LabelBadge labelClass="label-stock-transaction-in" v-if="row.data.direction == StockTransactionDirectionTypes.in">In</LabelBadge>
          <LabelBadge labelClass="label-stock-transaction-out" v-if="row.data.direction == StockTransactionDirectionTypes.out">Out</LabelBadge>
          <LabelBadge labelClass="label-stock-transaction-moved" v-if="row.data.direction == StockTransactionDirectionTypes.moved">Moved</LabelBadge>
        </td>
        <td>
          <LinkStock :id="row.data.stockId" :name="row.data.stock.code" class="font-weight-bold" />
        </td>
        <td>
          {{row.data.stockDetails | formatHyphenEmptyValue}}
        </td>
        <td>
          <span v-if="row.data.direction == StockTransactionDirectionTypes.moved">{{row.data.requestedChange}}</span>
          <span v-else>
            <span :class="['font-weight-bold', row.data.changeQuantity > 0 ? 'stock-transaction-in' : 'stock-transaction-out']">{{row.data.changeQuantity > 0 ? '+' : ''}}{{formatDecimal(row.data.changeQuantity)}}</span>
            <Tooltip v-if="formatDecimal(row.data.changeQuantity) != formatDecimal(row.data.requestedChange)" :label="`${formatDecimal(row.data.requestedChange)} requested but ${formatDecimal(row.data.changeQuantity)} made due to insufficient stock`"> ({{row.data.requestedChange > 0 ? '+' : ''}}{{formatDecimal(row.data.requestedChange)}})</Tooltip>
          </span>
        </td>
        <td>
          <span v-if="row.data.direction == StockTransactionDirectionTypes.moved">-</span>
          <span v-else>{{row.data.destQuantity}}</span>
        </td>
        <td>
          <template v-if="transactionHasLink(row.data)">
            <LinkPurchaseOrder v-if="row.data.type == StockTransactionTypes.purchaseOrder" :id="row.data.purchaseOrderId" :name="transactionTypeName(row.data)" />
            <LinkOrder v-if="row.data.type == StockTransactionTypes.salesOrder" :id="row.data.orderId" :name="transactionTypeName(row.data)" />
            <LinkStockTake v-if="row.data.type == StockTransactionTypes.stockTakeAdjust" :id="row.data.stockTakeId" :name="transactionTypeName(row.data)" />
          </template>
          <template v-else>
            {{transactionTypeName(row.data)}}
          </template>
        </td>
        <td>
          {{(isTransactionManualType(row.data) && row.data.manualReason) ? row.data.manualReason + ' - ': ''}}
          {{row.data.note}}
        </td>
        <td>
          {{row.data.sourceInventoryBin ? row.data.sourceInventoryBin.code : '-'}}
        </td>
        <td>
          <LabelAccountLocationBadge v-if="row.data.sourceInventoryBin && row.data.sourceInventoryBin.accountLocation" :accountLocation="row.data.sourceInventoryBin.accountLocation" />
        </td>
        <td>
          {{row.data.destInventoryBin ? row.data.destInventoryBin.code : '-'}}
        </td>
        <td>
          <LabelAccountLocationBadge v-if="row.data.destInventoryBin && row.data.destInventoryBin.accountLocation" :accountLocation="row.data.destInventoryBin.accountLocation" />
        </td>
        <td>
          <LinkUser v-if="row.data.user" :id="row.data.userId" :name="row.data.user.name" />
        </td>
        <td>
          {{row.data.createdAt | formatDateTime}}
        </td>
      </tr>
    </template>
  </DataTable>
</template>

<script>
import _ from 'lodash'
import Utils from '@/utils/utils'

import { StockTransactionDirectionTypes, StockTransactionTypes, StockTransactionTypeLookup } from '@/store/types'

export default {
  name: 'StockTransactionDataTable',
  props: {
    openNewTab: {
      type: Boolean,
      default: false
    },
    filterStockId: Number,
    filterPurchaseOrderId: Number
  },
  created() {
    this.StockTransactionDirectionTypes = StockTransactionDirectionTypes
    this.StockTransactionTypes = StockTransactionTypes
  },
  computed: {
    title() {
      return this.$route.meta.title
    },
    dataURL() {
      var params = {
        filter: {}
      }
      if(this.filterStockId)
        params.filter.stockId = this.filterStockId
      if(this.filterPurchaseOrderId)
        params.filter.purchaseOrderId = this.filterPurchaseOrderId

      return '/inventory/stocktransaction?' + $.param(params)
    },
    filterOptions() {
      var filterOptions = [{
        field: 'direction',
        name: 'Direction',
        options: [
          { name: 'In', value: StockTransactionDirectionTypes.in, class: 'label-stock-transaction-in' },
          { name: 'Out', value: StockTransactionDirectionTypes.out, class: 'label-stock-transaction-out' },
          { name: 'Moved', value: StockTransactionDirectionTypes.moved, class: 'label-stock-transaction-moved' },
        ]
      },
      {
        field: 'type',
        name: 'Type',
        columnIndex: 1,
        options: [
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.manual), value: StockTransactionTypes.manual, class: 'label-inventory-transtype-manual' }, 
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.blockOpened), value: StockTransactionTypes.blockOpened, class: 'label-inventory-transtype-blockOpened' }, 
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.purchaseOrder), value: StockTransactionTypes.purchaseOrder, class: 'label-inventory-transtype-purchaseOrder' }, 
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.salesOrder), value: StockTransactionTypes.salesOrder, class: 'label-inventory-transtype-salesOrder' }, 
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.processed), value: StockTransactionTypes.processed, class: 'label-inventory-transtype-processed' }, 
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.stockTakeAdjust), value: StockTransactionTypes.stockTakeAdjust, class: 'label-inventory-transtype-stockTakeAdjust' }, 
          { name: Utils.getLookupName(StockTransactionTypeLookup, StockTransactionTypes.offcut), value: StockTransactionTypes.offcut, class: 'label-inventory-transtype-offcut' }, 
        ]
      }]

      return filterOptions
    },
    locationFilterOptions() {
      var filterOptions = []

      filterOptions.push({
        name: 'Source Location',
        field: '$sourceInventoryBin.accountLocationId$',
        options: this.accountLocations.map(location => {
          return {
            name: location.name,
            value: location.id,
            colour: location.highlightColour
          }
        })
      })
      filterOptions.push({
        name: 'Dest Location',
        field: '$destInventoryBin.accountLocationId$',
        options: this.accountLocations.map(location => {
          return {
            name: location.name,
            value: location.id,
            colour: location.highlightColour
          }
        })
      })

      return filterOptions
    }
  },
  data() {
    return {
      columnList: [
        { title: "Direction", data: "direction" },
        { title: "Stock", data: "stock.code" },
        { title: "Details", data: "stockDetails" },
        { title: "Change", data: "requestedChange" },
        { title: "Now Available", data: "destQuantity" },
        { title: "Transaction Type", data: "type" },
        { title: "Notes", data: "note" },
        { title: "Source Bin", data: "sourceInventoryBin.code" },
        { title: "Source Location", data: "sourceInventoryBin.accountLocation.name" },
        { title: "Dest Bin", data: "destInventoryBin.code" },
        { title: "Dest Location", data: "destInventoryBin.accountLocation.name" },
        { title: "User", data: "userId" },
        { title: "Date", data: "createdAt", dateFilter: true, type: 'datetime' }
      ],
      accountLocations: []
    }
  },
  mounted() {
    this.requestData()
  },
  methods: {
    reload() {
      this.$refs.dataTable.reload()
      this.requestData()
    },
    requestData() {
      this.$apiService.adminService.getAccountLocations()
      .then(response => {
        this.accountLocations = response.data.filter(location => location.isActive)
      })
      .catch(err => {
        this.displayNetworkError(err, 'Error loading data')
      })
    },
    transactionHasLink(transaction) {
      var linkTypes = [StockTransactionTypes.purchaseOrder, StockTransactionTypes.salesOrder, StockTransactionTypes.stockTakeAdjust]
      return _.indexOf(linkTypes, transaction.type) != -1
    },
    transactionTypeName(transaction) {
      switch(transaction.type) {
        case StockTransactionTypes.purchaseOrder:
          if(transaction.purchaseOrder && transaction.purchaseOrder.sequenceNumber)
            return `Purchase Order - ${transaction.purchaseOrder.sequenceNumber}`
          break

        case StockTransactionTypes.salesOrder:
          if(transaction.order && transaction.order.sequenceNumber)
            return `Order - ${transaction.order.sequenceNumber}`
      }

      return Utils.getLookupName(StockTransactionTypeLookup, transaction.type)
    },
    isTransactionManualType(transaction) {
      return transaction.type == StockTransactionTypes.manual
    },
  }
};
</script>
